import { AnimatePresence, motion } from "framer-motion";
import { Copy, Edit2, Plus, Trash2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { createSector, deleteSector, getSectors, updateSector } from "../../services/api";

export function SectorManagement() {
    const [sectors, setSectors] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSector, setEditingSector] = useState(null);
    const [formData, setFormData] = useState({ name: "", description: "" });

    const fetchSectors = async () => {
        setIsLoading(true);
        try {
            const response = await getSectors();
            setSectors(response.data);
        } catch (error) {
            console.error("Erro ao buscar setores:", error);
            alert("Erro ao buscar setores");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchSectors();
    }, []);

    const handleOpenModal = (sector = null) => {
        if (sector) {
            setEditingSector(sector);
            setFormData({ name: sector.name, description: sector.description || "" });
        } else {
            setEditingSector(null);
            setFormData({ name: "", description: "" });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingSector(null);
        setFormData({ name: "", description: "" });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingSector) {
                await updateSector(editingSector.id, formData);
                alert("Setor atualizado com sucesso!");
            } else {
                await createSector(formData);
                alert("Setor criado com sucesso!");
            }
            handleCloseModal();
            fetchSectors();
        } catch (error) {
            console.error("Erro ao salvar setor:", error);
            alert("Erro ao salvar setor. Tente novamente.");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Tem certeza que deseja excluir este setor? Esta ação não pode ser desfeita.")) {
            try {
                await deleteSector(id);
                alert("Setor excluído com sucesso!");
                fetchSectors();
            } catch (error) {
                console.error("Erro ao excluir setor:", error);
                alert("Erro ao excluir setor. Verifique se existem leitos associados.");
            }
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Gerenciar Setores</h2>
                <button
                    onClick={() => handleOpenModal()}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                    <Plus size={20} />
                    Adicionar Setor
                </button>
            </div>

            <div className="bg-white/50 dark:bg-black/20 backdrop-blur-sm rounded-xl border border-gray-200/50 dark:border-gray-800 p-1">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-800">
                            <th className="p-4 font-medium text-gray-500 dark:text-gray-400">ID</th>
                            <th className="p-4 font-medium text-gray-500 dark:text-gray-400">Nome</th>
                            <th className="p-4 font-medium text-gray-500 dark:text-gray-400">Descrição</th>
                            <th className="p-4 font-medium text-gray-500 dark:text-gray-400 text-right">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        <AnimatePresence>
                            {sectors.map((sector) => (
                                <motion.tr
                                    key={sector.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0 }}
                                    className="border-b border-gray-100 dark:border-gray-800/50 last:border-0 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                                >
                                    <td className="p-4 text-gray-600 dark:text-gray-300">#{sector.id}</td>
                                    <td className="p-4 font-medium text-gray-800 dark:text-white">{sector.name}</td>
                                    <td className="p-4 text-gray-600 dark:text-gray-400">{sector.description || "-"}</td>
                                    <td className="p-4 text-right flex justify-end gap-2">
                                        <button
                                            onClick={() => handleOpenModal(sector)}
                                            className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                            title="Editar"
                                        >
                                            <Edit2 size={18} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(sector.id)}
                                            className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                            title="Excluir"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </td>
                                </motion.tr>
                            ))}
                        </AnimatePresence>
                        {sectors.length === 0 && !isLoading && (
                            <tr>
                                <td colSpan="4" className="p-8 text-center text-gray-500">
                                    Nenhum setor encontrado
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-6 w-full max-w-md border border-gray-200 dark:border-gray-800"
                    >
                        <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
                            {editingSector ? "Editar Setor" : "Novo Setor"}
                        </h3>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Nome do Setor
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white"
                                    placeholder="Ex: UTI Adulto"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Descrição
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white h-24 resize-none"
                                    placeholder="Descrição opcional..."
                                />
                            </div>
                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 rounded-lg transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                                >
                                    Salvar
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}
        </div>
    );
}
